import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { Column } from '@repo/ui/data-table'
import { EmailIcon, PlusIcon, UploadIcon, UserThreeIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import { useMutation } from '@tanstack/react-query'
import { useRef, useState } from 'react'
import { toast } from 'sonner'

import { API } from '../../api/apis.config'
import { sendEmailToStaffs } from '../../api/staff-application/send-email-to-staffs'
import { connectAsStaff } from '../../api/staffs/connect-as-staff'
import FilterDrawer from '../../components/elements/drawers/filter-drawer'
import DynamicDataTable, { DynamicTableRef } from '../../components/elements/dynamic-table'
import { countFilters } from '../../components/elements/dynamic-table/utils/countFilters'
import UserNavBar from '../../components/elements/nav-bar'
import SendEmailForm from '../../components/pages/candidates/send-email-form'
import { getUserDefaultRedirectionPath } from '../../components/pages/login/utils'
import { StaffsFilter } from '../../components/pages/staffs/staffs-filter'
import { Paths } from '../../lib/constants/path'
import { useDownloadDocuments } from '../../lib/hooks/use-download-documents'
import { useFilters } from '../../lib/hooks/use-filters'
import { useModalManager } from '../../lib/hooks/use-modal-manager'
import useTranslate from '../../lib/hooks/use-translate'
import { UserStatusEnum } from '../../lib/types/enums/users/user-status.enum'
import { Staff, StaffFilters } from '../../lib/types/models/staff/staff'
import { modalButtonConfig } from '../../lib/utils/buttons-config/validation-buttons-config'
import {
  transformStaffFiltersToUrl,
  transformUrlToStaffFilters
} from '../../lib/utils/filters-mapping/staff'
import { showAxiosError } from '../../lib/utils/functions/misc.functions'
import { getStaffColumns } from './utils'

export const staffInitialFilter: StaffFilters = {
  active: true
}

export function StaffsListPage() {
  const { navigate, navigateWithEvent } = useNavigator()
  const { downloadDocuments } = useDownloadDocuments()
  const [isTableLoading, setIsTableLoading] = useState(false)
  const [connectButtonId, setConnectButtonId] = useState<number>()
  const { t } = useTranslate()
  const tableRef = useRef<DynamicTableRef<Staff>>(null)

  const { selectedFilter, filter, onFilterChange, resetFilters, onSelectedFilterChange } =
    useFilters<StaffFilters>({
      initialFilters: staffInitialFilter,
      options: { allowSearchParam: true },
      transformFilterToUrl: transformStaffFiltersToUrl,
      transformUrlToFilter: transformUrlToStaffFilters
    })

  const { modalState, selectedData, toggleModal, closeModel } = useModalManager<
    'send-email' | 'filters',
    Staff[]
  >(['send-email', 'filters'])

  const { mutate: sendEmailToStaffsMutation, isPending: isEmailPending } = useMutation({
    mutationFn: sendEmailToStaffs,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Les emails ont été envoyés avec succès')
      closeModel('send-email')
    }
  })

  const { mutate: connectAsStaffMutation, isPending: connectAsStaffPending } = useMutation({
    mutationFn: connectAsStaff,
    onSuccess: (data) => {
      // A full page load: nothing of the previous session keeps running
      window.location.assign(getUserDefaultRedirectionPath(data))
    },
    onError: showAxiosError
  })

  const emails = selectedData?.map((row) => row.user.email) ?? []

  const personalSpaceColumn: Column<Staff> = {
    accessorKey: 'id',
    header: 'Espace encadrant',
    cell: ({ row }) => (
      <Button
        variant="primary"
        label="Voir"
        disabled={row.original.user?.status !== UserStatusEnum.ACTIVE}
        loading={connectAsStaffPending && connectButtonId === row.original.id}
        onClick={(event) => {
          event.stopPropagation()
          setConnectButtonId(row.original.id)
          connectAsStaffMutation(row.original.id)
        }}
      />
    )
  }

  return (
    <div>
      <UserNavBar title={'Liste des encadrants'} icon={UserThreeIcon} />
      <div className={'h-4'} />

      <DynamicDataTable<Staff, StaffFilters>
        ref={tableRef}
        title={'Tous les encadrants'}
        onLoadingChange={setIsTableLoading}
        columns={[...getStaffColumns('FULL', t), personalSpaceColumn]}
        selectable
        allowSearchParam
        onRowClick={(row, event) =>
          navigateWithEvent(Paths.STAFF_UPDATE_PAGE.replace(':id', row.id.toString()), event)
        }
        version={'FULL'}
        sorting={{ id: 'lastName', desc: false }}
        permissions={{ delete: false }}
        endpoint={API.staffs.getAll.url}
        filters={selectedFilter}
        filtersLength={countFilters(selectedFilter)}
        onFiltersClick={() => toggleModal('filters')}
        actions={[
          {
            type: 'global',
            icon: PlusIcon,
            label: 'Créer un nouvel encadrant',
            fn: () => navigate(Paths.STAFF_CREATE_PAGE)
          },
          {
            type: 'multiple',
            icon: EmailIcon,
            label: 'Envoyer un email personnalisé',
            variant: 'ghost',
            fn: (staff) => toggleModal('send-email', staff)
          },
          {
            type: 'multiple',
            icon: UploadIcon,
            refetch: true,
            variant: 'ghost',
            label: 'Exporter vers Excel',
            fn: (staffs) => {
              const ids = staffs.map((staff) => staff.id)
              downloadDocuments({ ids, endpoint: API.staffs.exportExcel.url })
            }
          }
        ]}
      />

      {/* Send email */}
      <Modal
        isOpen={modalState['send-email']}
        title={'Envoyer un email'}
        icon={EmailIcon}
        size="medium"
        hideActions
        onCancel={() => closeModel('send-email')}
      >
        {selectedData && (
          <SendEmailForm
            className={MODAL_BODY_CLASSNAME}
            defaultValues={{
              emails,
              subject: '',
              body: ''
            }}
            labels={selectedData.map(
              (staff) => `${toFullName(staff.firstName, staff.lastName)} - ${staff.user.email}`
            )}
            buttonsConfig={modalButtonConfig({
              onCancel: () => closeModel('send-email'),
              isLoading: isEmailPending,
              onConfirm: (values) => sendEmailToStaffsMutation({ ...values, emails })
            })}
          />
        )}
      </Modal>

      {/* Filters Modal */}
      <FilterDrawer<StaffFilters>
        isOpen={modalState.filters}
        selectedFilter={selectedFilter}
        isLoading={isTableLoading}
        onClose={() => closeModel('filters')}
        onReset={resetFilters}
        onApply={() => onSelectedFilterChange({ ...filter })}
        onFilterChange={onFilterChange}
      >
        <StaffsFilter filter={filter} onFilterChange={onFilterChange} />
      </FilterDrawer>
    </div>
  )
}
